import { normalizeText } from "../../../utils/normalizeText";
// import { Icon } from '@iconify-icon/react/dist/iconify.mjs';
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./style.css";
import AppIcon from "../../App/AppIcon";

interface CustomMultiSelectProps {
  value: string[];
  onChange: (val: string[]) => void;
  options: { label: string; value: string }[];
  showClear?: boolean;
  showSearch?: boolean;
  className?: string;
  error?: string;
  disabled?: boolean;
}

const CustomMultiSelect: React.FC<CustomMultiSelectProps> = ({
  value,
  onChange,
  options,
  showClear,
  showSearch,
  className = "",
  error,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showSelected, setShowSelect] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const selectRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLUListElement>(null);

  const isSelected = (val: string) => value.includes(val);

  const handleSelect = (val: string) => {
    if (isSelected(val)) {
      onChange(value.filter((item) => item !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const handleClear = () => {
    onChange([]);
    setIsOpen(false);
    setSearchQuery("");
  };

  const filteredOptions = options.filter((opt) => normalizeText(opt.label).includes(normalizeText(searchQuery)));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (selectRef.current && !selectRef.current.contains(target) && portalRef.current && !portalRef.current.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && selectRef.current) {
      const updatePosition = () => {
        const rect = selectRef.current?.getBoundingClientRect();
        if (rect) {
          setDropdownPosition({
            top: rect.bottom,
            left: rect.left,
            width: rect.width
          });
        }
      };
      
      // Update position immediately and after a short delay
      updatePosition();
      const timeoutId = setTimeout(updatePosition, 10);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  return (
    <div className={`custom-multiselect-container ${className}`} ref={selectRef}>
      <div onClick={() => (disabled ? {} : setIsOpen(!isOpen))} className={`custom-multiselect-trigger ${disabled ? "disabled" : ""}`}>
        <span className={value.length > 0 ? "" : "custom-multiselect-placeholder"}>
          {value.length > 0 ? `${value.length} ${value.length > 1 ? "selecionados" : "selecionado"}` : "Selecione..."}
        </span>
        <div className="custom-multiselect-actions">
          {showClear && value.length > 0 && !disabled && (
            <button
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="custom-multiselect-clear"
            >
              <AppIcon icon={"mdi:clear"} />
            </button>
          )}
          <span>
            <AppIcon icon={"raphael:arrowdown"} width={12} />
          </span>
        </div>
      </div>

      {showSelected && (
        <div className={`custom-multiselect-selected ${error ? "error" : ""}`}>
          {value.length > 0 && (
            <div className="custom-multiselect-selected-content">
              {value.length > 0
                ? options
                    .filter((item) => value.includes(item.value))
                    .map((item, index) => (
                      <span key={index} className="custom-multiselect-tag" onClick={() => handleSelect(item.value)}>
                        {item.label}
                      </span>
                    ))
                : "Nenhum item selecionado"}
            </div>
          )}
        </div>
      )}

      {isOpen &&
        createPortal(
          <ul
            ref={portalRef}
            className="custom-multiselect-dropdown"
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width || "auto",
            }}
          >
            {showSearch && (
              <li className="custom-multiselect-search">
                <input type="text" placeholder="Buscar..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              </li>
            )}
            {filteredOptions.map((opt) => (
              <li
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={`custom-multiselect-option ${isSelected(opt.value) ? "selected" : ""}`}
              >
                {opt.label}
              </li>
            ))}
          </ul>,
          document.body
        )}

      <p className="custom-multiselect-toggle" onClick={() => setShowSelect(!showSelected)}>
        {showSelected ? "esconder" : "mostrar"}
      </p>
    </div>
  );
};

export default CustomMultiSelect;
